import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { authFetch } from '../lib/auth';
import { meshColors as colors } from '../styles/mesh';

/**
 * V2RouteGuard — gate for `/v2/*`.
 *
 * V1 deprecation chunk K1: open /v2 beyond `requireAdmin`. Each role
 * gets its own scope inside V2:
 *
 *   admin / client_manager  → full V2 (no scoping at this layer; per-page
 *                             access is enforced by worker auth guards).
 *   partner / viewer        → must be on /v2/partner-portal/{their_id}.
 *                             Anywhere else under /v2 → redirect there.
 *   client_user             → must be on /v2/c/{their_slug}/...
 *                             Anywhere else under /v2 → redirect to their
 *                             portal. Slug is resolved via /api/clients/:id.
 *
 * Until the slug for a client_user resolves we render a brief spinner so
 * we never flicker through the wrong page.
 */
export function V2RouteGuard({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const location = useLocation();
  const [resolvedClientSlug, setResolvedClientSlug] = useState<string | null>(null);
  const [slugError, setSlugError] = useState<string | null>(null);

  // Resolve the client_user's slug from their clientId. Mirrors
  // ClientPortalRedirect — this is the same API response, just plumbed
  // here so we can decide where to send the user before mounting V2App.
  useEffect(() => {
    if (auth.loading) return;
    if (!auth.isClientUser && !auth.isClientManager) return;
    // Admin/client_manager don't need this lookup. Only client_user is
    // hard-locked to a single slug.
    if (!auth.isClientUser) return;
    if (resolvedClientSlug) return;

    const id = auth.clientId ?? auth.assignedClientIds?.[0] ?? null;
    if (!id) {
      setSlugError('No client assigned to your account.');
      return;
    }

    let cancelled = false;
    authFetch(`/api/clients/${id}`)
      .then(res => (res.ok ? res.json() : null))
      .then((raw: unknown) => {
        if (cancelled) return;
        const data = raw as { slug?: string; client?: { slug?: string } } | null;
        const slug = data?.slug || data?.client?.slug || null;
        if (slug) setResolvedClientSlug(slug);
        else setSlugError('Client not found.');
      })
      .catch(() => {
        if (!cancelled) setSlugError('Failed to load client information.');
      });
    return () => { cancelled = true; };
  }, [
    auth.loading,
    auth.isClientUser,
    auth.isClientManager,
    auth.clientId,
    auth.assignedClientIds,
    resolvedClientSlug,
  ]);

  if (auth.loading) return <Spinner />;

  if (!auth.user) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }
  if (!auth.profile) {
    return <ConfigError />;
  }

  // ─── Admin & client_manager: full pass.
  // Use actualIsAdmin so an admin can navigate freely even when
  // viewing-as-partner; otherwise the partner scope below would trap them.
  // client_manager is broader than client_user — they can see any of
  // their assigned clients, so we don't pin them to a single slug here.
  // Per-page worker guards (requireClientAccess / requireSensitiveAccess)
  // still constrain what their queries return.
  if (auth.actualIsAdmin || auth.isClientManager) {
    return <>{children}</>;
  }

  // ─── Partner / viewer: pin to their partner-portal.
  if (auth.isPartner) {
    const partnerId = auth.partnerId;
    if (!partnerId) {
      // No partner_id on profile — same fallback as PortalRedirect:
      // bounce to /portal which surfaces the dedicated error UX.
      return <Navigate to="/portal" replace />;
    }
    const expected = `/v2/partner-portal/${partnerId}`;
    const onScope = location.pathname === expected
      || location.pathname.startsWith(`${expected}/`);
    if (!onScope) {
      return <Navigate to={expected} replace />;
    }
    return <>{children}</>;
  }

  // ─── client_user: pin to their `/v2/c/{slug}/portal` page only.
  //
  // Earlier we allowed the entire `/v2/c/{slug}/*` subtree, but that
  // included admin-class pages (rules, attribution, fraud-rules, settings,
  // pipelines, audit, …). Per-page worker auth guards refuse to serve
  // their data, but the UI still rendered, which was both a confusing
  // experience and a soft information-leak risk (route exists, sidebar
  // shows it, etc.). Lock it down to the dedicated client portal page
  // — that's the only V2 surface client_users are intended to see.
  //
  // Allowed: `/v2/c/{slug}/portal` (and subroutes within the portal,
  // since the portal itself has tabs that don't change pathname today
  // but might add nested routes later — keep that future-proof).
  if (auth.isClientUser) {
    if (slugError) return <SlugError message={slugError} />;
    if (!resolvedClientSlug) return <Spinner />;
    const portalPath = `/v2/c/${resolvedClientSlug}/portal`;
    const onPortal = location.pathname === portalPath
      || location.pathname.startsWith(`${portalPath}/`);
    if (!onPortal) {
      return <Navigate to={portalPath} replace />;
    }
    return <>{children}</>;
  }

  // Authenticated but unrecognized role — kick to login.
  return <Navigate to="/auth/login" replace />;
}

function Spinner() {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: colors.bgPrimary,
        fontFamily: colors.fontSans,
        color: colors.textPrimary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Loader2
        size={32}
        style={{ color: colors.brand, animation: 'spin 1s linear infinite' }}
      />
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function ConfigError() {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: colors.bgPrimary,
        fontFamily: colors.fontSans,
        color: colors.textPrimary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      <div style={{ textAlign: 'center', maxWidth: '400px' }}>
        <h2 style={{ color: colors.error, marginBottom: '12px' }}>Configuration Error</h2>
        <p style={{ color: colors.textSecondary }}>
          Your account does not have a profile configured. Contact an administrator.
        </p>
      </div>
    </div>
  );
}

function SlugError({ message }: { message: string }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: colors.bgPrimary,
        fontFamily: colors.fontSans,
        color: colors.textPrimary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      <div style={{ textAlign: 'center', maxWidth: '420px' }}>
        <h2 style={{ color: colors.error, marginBottom: '12px' }}>Access Error</h2>
        <p style={{ color: colors.textSecondary, marginBottom: '12px' }}>{message}</p>
        <a href="/auth/login" style={{ color: colors.link }}>Back to login</a>
      </div>
    </div>
  );
}
